import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { celebrateGift } from '../../utils/confetti';
import { SuccessCheckmark } from '../../components/Animations';
import ShareGiftCard from '../../components/ShareGiftCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GiftSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [giftData, setGiftData] = useState(null);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      navigate('/wallet');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    try {
      const { data } = await axios.get(
        `${API_URL}/api/payment/checkout/status/${sessionId}`,
        { withCredentials: true }
      );

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setGiftData(data.gift_card || {});
        toast.success('Gift card sent successfully!');
        setChecking(false);
        // Trigger celebration confetti!
        setTimeout(() => celebrateGift(), 300);
      } else if (data.status === 'expired') {
        setPaymentStatus('failed');
        toast.error('Payment session expired');
        setChecking(false);
      } else if (attempts < maxAttempts) {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setPaymentStatus('pending');
        setChecking(false);
      }
    } catch (error) {
      if (attempts < maxAttempts) {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setPaymentStatus('failed');
        toast.error('Failed to verify payment');
        setChecking(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#18261E] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <motion.div 
          className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl p-8 text-center"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
        >
          {checking ? (
            <div data-testid="checking-status">
              <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-gray-300 border-r-transparent mb-4"></div>
              <h2 className="text-2xl tracking-tight text-white mb-2">Processing Payment</h2>
              <p className="text-[#889990]">Please wait while we confirm your gift card...</p>
            </div>
          ) : paymentStatus === 'success' ? (
            <motion.div 
              data-testid="success-status"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex justify-center mb-6">
                <SuccessCheckmark size={80} />
              </div>
              <h2 className="text-2xl tracking-tight text-white mb-2">Gift Sent!</h2>
              <p className="text-[#889990] mb-6">Your gift card has been sent successfully</p>
              
              {/* Gift card preview */}
              <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between items-center">
                  <span className="text-[#889990] text-sm">Amount</span>
                  <span className="text-white font-medium">£{giftData?.amount?.toFixed(2) || '0.00'}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <motion.button
                  onClick={() => setShowShare(true)}
                  className="flex-1 bg-[#344840]/50 text-white border border-[#3E5248] hover:bg-gray-600/50 px-6 py-3 rounded-full font-medium transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="share-gift-button"
                >
                  Share
                </motion.button>
                <motion.button
                  onClick={() => navigate('/wallet')}
                  className="flex-1 bg-[#C3A365] text-[#18261E] hover:bg-white px-6 py-3 rounded-full font-medium transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="return-wallet-button"
                >
                  View Wallet
                </motion.button>
              </div>
            </motion.div>
          ) : (
            <div data-testid="failed-status">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl tracking-tight text-white mb-2">Payment Issue</h2>
              <p className="text-[#889990] mb-6">There was an issue with your payment. Please try again.</p>
              <div className="flex gap-4">
                <motion.button
                  onClick={() => navigate('/send-gift')}
                  className="flex-1 bg-[#C3A365] text-[#18261E] hover:bg-white px-6 py-3 rounded-full font-medium transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="try-again-button"
                >
                  Try Again
                </motion.button>
                <motion.button
                  onClick={() => navigate('/wallet')}
                  className="flex-1 bg-transparent text-white border border-[#3E5248] hover:border-[#3E5248] px-6 py-3 rounded-full font-medium transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="cancel-button"
                >
                  Cancel
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Share Modal */}
      {showShare && (
        <ShareGiftCard
          giftCode={giftData?.code || ''}
          amount={giftData?.amount || 0}
          recipientName={giftData?.recipient_name}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
};

export default GiftSuccess;
